/** Admin inbox detail with notes + status + convert-to-model. */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Envelope,
  Phone,
  XLogo,
  InstagramLogo,
  TiktokLogo,
  ArrowsClockwise,
  Trash,
  UserPlus,
} from "@phosphor-icons/react";
import { inboxApi, INBOX_STATUSES } from "../../api/inbox";
import { MODEL_STATUSES } from "../../api/models";
import Modal from "../../components/ui-shared/Modal";

const FIELD =
  "rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white w-full";
const LABEL =
  "block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5";

function InfoRow({ icon: Icon, label, value, link }) {
  if (!value) return null;
  const content = link ? (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm text-slate-900 hover:underline"
    >
      {value}
    </a>
  ) : (
    <span className="text-sm text-slate-900">{value}</span>
  );
  return (
    <div className="flex items-start gap-3 py-2">
      {Icon && (
        <Icon size={15} weight="regular" className="text-slate-400 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
          {label}
        </p>
        <div className="mt-0.5">{content}</div>
      </div>
    </div>
  );
}

function ConvertForm({ submission, onCancel, onDone }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("prospect");
  const [createAccount, setCreateAccount] = useState(false);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const canAccount = status === "active" || status === "inactive";

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      if (createAccount && (!canAccount || password.length < 8)) {
        setError(t("inbox.convert.error_account"));
        return;
      }
      setSaving(true);
      try {
        const payload = {
          status,
          create_account: createAccount && canAccount,
        };
        if (payload.create_account) payload.password = password;
        const res = await inboxApi.convert(submission.id, payload);
        onDone?.(res);
      } catch (err) {
        setError(err?.message || t("inbox.convert.error_generic"));
      } finally {
        setSaving(false);
      }
    },
    [submission.id, status, createAccount, canAccount, password, onDone, t],
  );

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="convert-form">
      <p className="text-sm text-slate-600">
        {t("inbox.convert.intro", {
          name: `${submission.first_name} ${submission.last_name || ""}`.trim(),
        })}
      </p>
      <div>
        <label className={LABEL}>{t("inbox.convert.status")}</label>
        <select
          data-testid="convert-status"
          className={FIELD}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {MODEL_STATUSES.filter(
            (s) => s === "prospect" || s === "active" || s === "inactive",
          ).map((s) => (
            <option key={s} value={s}>
              {t(`models.status.${s}`)}
            </option>
          ))}
        </select>
      </div>
      {canAccount && (
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 space-y-2">
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              data-testid="convert-create-account"
              checked={createAccount}
              onChange={(e) => setCreateAccount(e.target.checked)}
            />
            {t("models.new.create_account")}
          </label>
          {createAccount && (
            <input
              type="password"
              data-testid="convert-password"
              placeholder={t("models.new.password_placeholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={FIELD}
            />
          )}
        </div>
      )}
      {error && (
        <p data-testid="convert-error" className="text-xs text-red-600">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
        >
          {t("common.cancel")}
        </button>
        <button
          type="submit"
          data-testid="convert-submit"
          disabled={saving}
          className="rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {saving ? t("common.loading") : t("inbox.convert.submit")}
        </button>
      </div>
    </form>
  );
}

export default function AdminInboxDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [convertOpen, setConvertOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await inboxApi.get(id);
      setSub(data);
      setNotes(data.admin_notes || "");
      // Auto-flip to reviewing on first open
      if (data.status === "new") {
        await inboxApi.update(id, { status: "reviewing" });
        setSub({ ...data, status: "reviewing" });
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = useCallback(
    async (next) => {
      await inboxApi.update(id, { status: next });
      setSub((s) => ({ ...s, status: next }));
    },
    [id],
  );

  const saveNotes = useCallback(async () => {
    setSavingNotes(true);
    try {
      await inboxApi.update(id, { admin_notes: notes });
      setSavedAt(new Date());
    } finally {
      setSavingNotes(false);
    }
  }, [id, notes]);

  const remove = useCallback(async () => {
    if (!window.confirm(t("inbox.delete_confirm"))) return;
    await inboxApi.remove(id);
    navigate("/admin/inbox");
  }, [id, navigate, t]);

  if (loading || !sub)
    return <p className="text-sm text-slate-500">{t("common.loading")}</p>;

  return (
    <div data-testid="admin-inbox-detail" className="space-y-8">
      <Link
        to="/admin/inbox"
        data-testid="inbox-back"
        className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft size={14} weight="bold" />
        {t("inbox.title")}
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
            {t(`contact.form.motif_${sub.motif}`, { defaultValue: sub.motif })}{" "}
            · {new Date(sub.created_at).toLocaleString(i18n.language || "fr")}
          </p>
          <h1 className="mt-1 font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
            {sub.first_name} {sub.last_name}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="inbox-status-select"
            value={sub.status}
            onChange={(e) => changeStatus(e.target.value)}
            className={FIELD + " w-auto"}
          >
            {INBOX_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`inbox.status.${s}`)}
              </option>
            ))}
          </select>
          {sub.status !== "converted" && (
            <button
              type="button"
              data-testid="inbox-convert-btn"
              onClick={() => setConvertOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              <UserPlus size={13} weight="bold" />
              {t("inbox.convert.button")}
            </button>
          )}
          {sub.status === "converted" && sub.converted_model_id && (
            <Link
              to={`/admin/models/${sub.converted_model_id}/profile`}
              data-testid="inbox-open-converted"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-900"
            >
              <ArrowsClockwise size={13} weight="regular" />
              {t("inbox.open_converted")}
            </Link>
          )}
          <button
            type="button"
            data-testid="inbox-delete"
            onClick={remove}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <Trash size={13} weight="regular" />
            {t("common.delete")}
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Identity & contact */}
        <section className="lg:col-span-1 rounded-2xl border border-slate-200 bg-white p-5">
          <p className={LABEL}>{t("inbox.section.identity")}</p>
          <div className="divide-y divide-slate-100">
            <InfoRow
              icon={Envelope}
              label={t("contact.form.email_label")}
              value={sub.email}
              link={`mailto:${sub.email}`}
            />
            <InfoRow
              icon={Phone}
              label={t("contact.form.phone_label")}
              value={sub.phone}
              link={`tel:${sub.phone.replace(/\s/g, "")}`}
            />
            <InfoRow
              icon={XLogo}
              label="X"
              value={sub.x_handle ? `@${sub.x_handle}` : null}
              link={sub.x_handle ? `https://x.com/${sub.x_handle}` : null}
            />
            <InfoRow
              icon={InstagramLogo}
              label="Instagram"
              value={
                sub.instagram_handle ? `@${sub.instagram_handle}` : null
              }
              link={
                sub.instagram_handle
                  ? `https://instagram.com/${sub.instagram_handle}`
                  : null
              }
            />
            <InfoRow
              icon={TiktokLogo}
              label="TikTok"
              value={sub.tiktok_handle ? `@${sub.tiktok_handle}` : null}
              link={
                sub.tiktok_handle
                  ? `https://tiktok.com/@${sub.tiktok_handle}`
                  : null
              }
            />
          </div>
        </section>

        {/* Message & notes */}
        <section className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className={LABEL}>{t("inbox.section.message")}</p>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
              {sub.message}
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className={LABEL}>{t("inbox.section.notes")}</p>
            <textarea
              data-testid="inbox-notes"
              rows={6}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("inbox.notes_placeholder")}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white resize-y"
            />
            <div className="mt-2 flex items-center justify-end gap-3">
              {savedAt && (
                <span className="text-xs text-emerald-600">
                  {t("admin.courses.saved_at", {
                    time: savedAt.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                  })}
                </span>
              )}
              <button
                type="button"
                onClick={saveNotes}
                disabled={savingNotes}
                data-testid="inbox-notes-save"
                className="rounded-full bg-slate-900 px-4 py-2 text-xs font-medium text-white disabled:opacity-40"
              >
                {savingNotes ? t("common.loading") : t("common.save")}
              </button>
            </div>
          </div>
        </section>
      </div>

      <Modal
        open={convertOpen}
        onClose={() => setConvertOpen(false)}
        title={t("inbox.convert.title")}
      >
        <ConvertForm
          submission={sub}
          onCancel={() => setConvertOpen(false)}
          onDone={async () => {
            setConvertOpen(false);
            await load();
          }}
        />
      </Modal>
    </div>
  );
}
